import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  IconButton,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Bell } from "lucide-react";
import { useHistory } from "react-router-dom";
import moment from "moment";

import ChatiaPopover from "../ChatiaPopover";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    tabsBar: {
      borderBottom: isDark ? "1px solid #2a2a2a" : "1px solid #e8e8e8",
      minHeight: 44,
      "& .MuiTab-root": {
        minHeight: 44,
        textTransform: "none",
        fontSize: 12,
        fontWeight: 500,
        minWidth: 0,
        flex: 1,
        padding: "10px 8px 6px",
      },
      "& .MuiTabs-indicator": {
        height: 2,
        backgroundColor: theme.palette.primary.main,
      },
    },
    tabBadge: {
      "& .MuiBadge-badge": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText || "#fff",
        top: 6,
        right: -2,
      },
    },
    listWrap: {
      width: "100%",
      padding: 4,
      boxSizing: "border-box",
    },
    item: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      padding: "10px 12px",
      borderRadius: 8,
      cursor: "pointer",
      transition: "background 0.15s ease",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
      },
    },
    itemTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#111b21",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    itemMeta: {
      fontSize: 11,
      color: isDark ? "#8696a0" : "#667781",
    },
    itemBody: {
      fontSize: 12,
      color: isDark ? "#cdd2d4" : "#54656f",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    },
    empty: {
      padding: 24,
      textAlign: "center",
      fontSize: 13,
      color: isDark ? "#8696a0" : "#667781",
    },
    loadingWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    },
    blueBadge: {
      "& .MuiBadge-badge": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText || "#fff",
      },
    },
  };
});

// === Aba: Notificações de Chat (tickets com mensagens não lidas) ===
const ChatTab = ({ classes, onClose }) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { tickets } = useTickets({ withUnreadMessages: "true" });

  const visible = useMemo(() => {
    const queueIds = (user?.queues || []).map((q) => q.id);
    return (tickets || []).filter((t) => {
      if (t.status === "pending" && user?.profile !== "admin") return false;
      if (queueIds.length > 0 && t.queueId && !queueIds.includes(t.queueId)) {
        return false;
      }
      return true;
    });
  }, [tickets, user]);

  if (!visible.length) {
    return <div className={classes.empty}>{i18n.t("notifications.noTickets")}</div>;
  }

  return (
    <div className={classes.listWrap}>
      {visible.map((t) => (
        <div
          key={t.id}
          className={classes.item}
          onClick={() => {
            onClose?.();
            history.push(`/tickets/${t.uuid}`);
          }}
        >
          <span className={classes.itemTitle}>
            {t.contact?.name || t.contact?.number || `Ticket #${t.id}`}
          </span>
          <span className={classes.itemBody}>
            {t.lastMessage || i18n.t("kanban.card.noMessages")}
          </span>
          <span className={classes.itemMeta}>
            {moment(t.updatedAt).fromNow()}
          </span>
        </div>
      ))}
    </div>
  );
};

// === Aba: Sistema (comunicados/announcements) ===
const SystemTab = ({ classes, onClose, onItemClick }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/announcements", {
          params: { pageNumber: 1, status: "active" },
        });
        if (!alive) return;
        setItems(data?.records || []);
      } catch (err) {
        if (alive) toastError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={20} />
      </div>
    );
  }

  if (!items.length) {
    return <div className={classes.empty}>{i18n.t("mainDrawer.appBar.notRegister")}</div>;
  }

  return (
    <div className={classes.listWrap}>
      {items.map((a) => (
        <div
          key={a.id}
          className={classes.item}
          onClick={() => {
            onItemClick?.(a);
            onClose?.();
          }}
        >
          <span className={classes.itemTitle}>{a.title}</span>
          {a.text && <span className={classes.itemBody}>{a.text}</span>}
          <span className={classes.itemMeta}>
            {moment(a.createdAt).format("DD/MM/YYYY")}
          </span>
        </div>
      ))}
    </div>
  );
};

const UnifiedNotifications = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tab, setTab] = useState(1); // default: Chat
  const triggerRef = useRef(null);

  const [systemUnread, setSystemUnread] = useState(0);

  const { tickets } = useTickets({ withUnreadMessages: "true" });
  const chatUnread = (tickets || []).length;

  // contador de comunicados (best-effort)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: ann } = await api.get("/announcements", {
          params: { pageNumber: 1, status: "active" },
        });
        if (!alive) return;
        setSystemUnread((ann?.records || []).length);
      } catch {
        // silencioso — só pra badge inicial
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const total = chatUnread + systemUnread;

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        ref={triggerRef}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{
          color: theme.mode === "dark" ? "#e9edef" : "#1a1a1a",
          padding: 8,
        }}
      >
        <Badge
          badgeContent={total}
          overlap="rectangular"
          max={99}
          className={classes.blueBadge}
        >
          <Bell size={20} strokeWidth={1.5} />
        </Badge>
      </IconButton>

      <ChatiaPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        title={i18n.t("notifications.title")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        paperStyle={{ width: 380 }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabsBar}
        >
          <Tab
            label={
              <Badge
                className={classes.tabBadge}
                badgeContent={systemUnread}
                max={99}
              >
                <span style={{ paddingRight: systemUnread ? 14 : 0 }}>
                  {i18n.t("notifications.tabs.system")}
                </span>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge
                className={classes.tabBadge}
                badgeContent={chatUnread}
                max={99}
              >
                <span style={{ paddingRight: chatUnread ? 14 : 0 }}>
                  {i18n.t("notifications.tabs.chat")}
                </span>
              </Badge>
            }
          />
        </Tabs>

        {tab === 0 && <SystemTab classes={classes} onClose={handleClose} />}
        {tab === 1 && <ChatTab classes={classes} onClose={handleClose} />}
      </ChatiaPopover>
    </>
  );
};

export default UnifiedNotifications;
